import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PricingTable from '../components/PricingTable';
import { useAuth } from '../contexts/AuthContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Info, ShieldCheck, Rocket, Lock } from 'lucide-react';
import { startRazorpayCheckout, startBetaCheckout } from '../lib/razorpay';

export default function Pricing() {
    const nav = useNavigate();
    const { user } = useAuth();
    const cfg = useAppConfig();
    const [autopay, setAutopay] = useState(false);

    const handleSelect = async (plan_id) => {
        if (!user) { nav('/login'); return; }
        if (cfg.beta_active) {
            // Every card routes to the flat-$15 beta gate during the beta window.
            await startBetaCheckout({});
            return;
        }
        await startRazorpayCheckout({ type: 'plan', plan_id, autopay });
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-16">
            <div className="max-w-3xl">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Pricing</p>
                <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-medium tracking-tighter">Choose your transit pipe.</h1>
                <p className="mt-4 text-muted-foreground">Predictable monthly costs. Cancel anytime.</p>
            </div>

            {/* Beta pre-checkout marketing banner — only during beta window */}
            {cfg.beta_active && !cfg.beta_full && (
                <div
                    data-testid="beta-precheckout-banner"
                    className="mt-10 border border-[#FF6B35] bg-gradient-to-br from-[#FF6B35]/15 to-transparent p-8 sm:p-10 flex flex-col lg:flex-row gap-8 items-start"
                >
                    <div className="flex-1">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 grid place-items-center bg-[#FF6B35] text-white">
                                <Rocket className="h-5 w-5" strokeWidth={2.4} />
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.32em] text-[#FF6B35]">Founding tester program</p>
                        </div>
                        <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-medium tracking-tight leading-tight">
                            Become a Founding Tester <span aria-hidden>🚀</span>
                        </h2>
                        <p className="mt-4 text-sm sm:text-base leading-relaxed text-muted-foreground max-w-3xl">
                            Join the Paid Beta for a flat <strong className="text-foreground">${cfg.beta_flat_price_usd} today</strong> to
                            help us battle-test the pipeline. As a huge thank you for your early trust, your first month of our official
                            premium subscription will be <strong className="text-[#FF6B35]">50% OFF</strong> from our side!
                        </p>
                    </div>
                    <div className="w-full lg:w-auto lg:min-w-[280px] flex flex-col gap-3">
                        <div className="border border-border bg-card px-5 py-4">
                            <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Beta seats remaining</p>
                            <p className="mt-1 font-heading text-3xl">{cfg.beta_slots_available}
                                <span className="text-base text-muted-foreground font-normal"> / {cfg.beta_user_limit}</span>
                            </p>
                        </div>
                        <button
                            data-testid="beta-cta-primary"
                            onClick={() => {
                                if (!user) { nav('/login'); return; }
                                startBetaCheckout({});
                            }}
                            className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white h-12 uppercase tracking-[0.15em] text-xs"
                        >
                            Claim my ${cfg.beta_flat_price_usd} founding seat →
                        </button>
                    </div>
                </div>
            )}

            {/* Beta full overlay */}
            {cfg.beta_active && cfg.beta_full && (
                <div
                    data-testid="beta-full-overlay"
                    className="mt-10 border border-[#FF6B35]/50 bg-card p-10 text-center"
                >
                    <Lock className="h-8 w-8 text-[#FF6B35] mx-auto" />
                    <h2 className="mt-5 font-heading text-3xl font-medium tracking-tight">Beta Full!</h2>
                    <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
                        Our developers are working hard. Official launch happening soon — stay tuned!
                    </p>
                </div>
            )}

            {/* Autopay toggle — hidden during beta (flat pricing, no autopay concept) */}
            {!cfg.beta_active && (
                <Card className="mt-10 p-6 border-border bg-card flex items-start justify-between gap-6 flex-wrap" data-testid="autopay-toggle-card">
                    <div className="flex items-start gap-3 max-w-2xl">
                        <div className="h-9 w-9 border border-border grid place-items-center shrink-0">
                            <ShieldCheck className="h-4 w-4 text-[#FF6B35]" />
                        </div>
                        <div>
                            <Label htmlFor="autopay-switch" className="text-base font-medium cursor-pointer">Enable Auto-Renewal (Optional)</Label>
                            <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                                Indian users: activate UPI Autopay / e-mandate for hands-free monthly renewal.
                                International users: your card will be charged automatically each month. Leave off for a strict one-time payment.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`text-xs uppercase tracking-[0.15em] ${autopay ? 'text-muted-foreground' : 'text-foreground'}`}>Manual</span>
                        <Switch
                            id="autopay-switch"
                            data-testid="autopay-switch"
                            checked={autopay}
                            onCheckedChange={setAutopay}
                        />
                        <span className={`text-xs uppercase tracking-[0.15em] ${autopay ? 'text-[#FF6B35]' : 'text-muted-foreground'}`}>Auto-pay</span>
                    </div>
                </Card>
            )}

            {/* Transparent billing promise — retail only */}
            {!cfg.beta_active && (
                <div className="mt-6 border border-[#FF6B35]/30 bg-[#FF6B35]/5 p-5 flex gap-3 items-start" data-testid="billing-promise">
                    <Info className="h-4 w-4 text-[#FF6B35] shrink-0 mt-0.5" />
                    <p className="text-sm leading-relaxed">
                        <strong className="text-[#FF6B35]">Our Transparent Billing Promise:</strong>{' '}
                        We never force automatic charges. Unless you check the &quot;Enable Auto-Renewal&quot; option, you will pay manually via email.
                        If a payment is missed, your account enters a <strong>7-day Grace Period</strong> where your rolled-over data stays perfectly safe and intact.
                        New uploads will be temporarily locked during this week, and files will reset if unpaid by Day 7.
                    </p>
                </div>
            )}

            <div className="mt-12">
                <PricingTable
                    onSelect={handleSelect}
                    betaActive={cfg.beta_active}
                    betaFull={cfg.beta_full}
                    betaPriceUsd={cfg.beta_flat_price_usd}
                />
            </div>
        </div>
    );
}
